import { baseApi } from '@app/baseApi';

import type {
  ConfigTemplate,
  CreateEnvironmentRequest,
  Credential,
  Environment,
  EnvironmentDependency,
  EnvironmentOverview,
  ExecutionLog,
  FileSchema,
  IntegrationProvider,
  IntegrationProviderProperty,
  IntegrationTypesResponse,
  SaveCredentialRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  UpdateEnvironmentRequest,
  UploadPolicyResponse,
} from '@/types';

import {
  transformBvaOAuthRequest,
  transformCreateEnvironmentRequest,
  transformCreateEnvironmentResponse,
  transformGetCredentialsResponse,
  transformGetCredentialsResponseNoDedupe,
  transformGetEnvironmentOverviewResponse,
  transformGetEnvironmentsResponse,
  transformGetFileSchemaResponse,
  transformSaveCredentialsRequest,
  transformTestConnectionRequest,
  transformTestConnectionResponse,
  transformUpdateEnvironmentRequest,
} from './environments.mappers';

/**
 * Normalize credentials before sending to TEST_CONN_API_URL.
 * Matches old UI addLeadingSlashToSftpPath (environmentActions.js line 753-765):
 * - SFTP path: ensure leading slash
 * - LingkAdapter: map rootUrl → endpoint
 * - Database: lowercase databaseType
 */
const normalizeCredentialsForTestConn = (
  creds: Record<string, unknown>
): Record<string, unknown> => {
  const normalized = { ...creds };
  if (typeof normalized.path === 'string' && normalized.path[0] !== '/') {
    normalized.path = `/${normalized.path}`;
  }
  if (normalized.rootUrl) {
    normalized.endpoint = normalized.rootUrl;
  }
  if (typeof normalized.databaseType === 'string') {
    normalized.databaseType = normalized.databaseType.toLowerCase();
  }
  return normalized;
};

export const environmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get environments for an app
    getEnvironments: builder.query<Environment[], { tenantId: string; appId: string }>({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/environments`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetEnvironmentsResponse,
      providesTags: (result, _error, { appId }) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'Connector' as const,
                id: `env-${id}`,
              })),
              { type: 'Connector', id: `app-${appId}` },
            ]
          : [{ type: 'Connector', id: `app-${appId}` }],
    }),

    // Get environment overview
    getEnvironmentOverview: builder.query<
      EnvironmentOverview,
      { tenantId: string; appId: string; envName: string }
    >({
      query: ({ tenantId, appId, envName }) => ({
        url: `${tenantId}/${appId}/environments/${envName}/overview`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetEnvironmentOverviewResponse,
    }),

    // Get environment dependencies
    getEnvironmentDependencies: builder.query<
      EnvironmentDependency[],
      { tenantId: string; appId: string; envId: string }
    >({
      query: ({ tenantId, appId, envId }) => ({
        url: `${tenantId}/${appId}/environment/deps/${envId}`,
        endpoint: 'transformer',
      }),
    }),

    // Create environment
    createEnvironment: builder.mutation<
      Environment,
      { tenantId: string; appId: string; data: CreateEnvironmentRequest }
    >({
      query: ({ tenantId, appId, data }) => ({
        url: `${tenantId}/${appId}/environments`,
        endpoint: 'transformer',
        method: 'POST',
        body: transformCreateEnvironmentRequest(data),
      }),
      transformResponse: transformCreateEnvironmentResponse,
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'Connector', id: `app-${appId}` }],
    }),

    // Update environment
    updateEnvironment: builder.mutation<
      void,
      { tenantId: string; appId: string; envId: string; data: UpdateEnvironmentRequest }
    >({
      query: ({ tenantId, appId, envId, data }) => ({
        url: `${tenantId}/${appId}/environments/${envId}`,
        endpoint: 'transformer',
        method: 'PUT',
        body: transformUpdateEnvironmentRequest(data),
      }),
      invalidatesTags: (_result, _error, { appId, envId }) => [
        { type: 'Connector', id: `env-${envId}` },
        { type: 'Connector', id: `app-${appId}` },
      ],
    }),

    // Delete environment
    deleteEnvironment: builder.mutation<void, { tenantId: string; appId: string; envId: string }>({
      query: ({ tenantId, appId, envId }) => ({
        url: `${tenantId}/${appId}/environments/${envId}`,
        endpoint: 'transformer',
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { appId, envId }) => [
        { type: 'Connector', id: `env-${envId}` },
        { type: 'Connector', id: `app-${appId}` },
      ],
    }),

    // Pause/Resume environment
    toggleEnvironmentPause: builder.mutation<
      void,
      { tenantId: string; appId: string; envId: string; paused: boolean }
    >({
      query: ({ tenantId, appId, envId, paused }) => ({
        url: `${tenantId}/${appId}/pauseenvironment/${envId}`,
        endpoint: 'transformer',
        method: 'PUT',
        body: { pauseSchedules: paused },
      }),
      invalidatesTags: (_result, _error, { envId, appId }) => [
        { type: 'Connector', id: `env-${envId}` },
        { type: 'Connector', id: `app-${appId}` },
      ],
    }),

    // Reorder environments
    reorderEnvironments: builder.mutation<
      void,
      { tenantId: string; appId: string; indexes: Record<string, number> }
    >({
      query: ({ tenantId, appId, indexes }) => ({
        url: `${tenantId}/${appId}/environmentorder`,
        endpoint: 'transformer',
        method: 'POST',
        body: indexes,
      }),
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'Connector', id: `app-${appId}` }],
    }),

    // Get environment credentials
    getCredentials: builder.query<
      Credential[],
      { tenantId: string; appId: string; envName: string }
    >({
      query: ({ tenantId, appId, envName }) => ({
        url: `${tenantId}/${appId}/environments/${envName}/credentials`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetCredentialsResponse,
      providesTags: (_result, _error, { appId, envName }) => [
        { type: 'Connector', id: `app-${appId}` },
        { type: 'Connector', id: `env-${envName}` },
      ],
    }),

    // Get credentials without dedupe (for cleanup hook to detect duplicate LocalFile entries)
    getCredentialsRaw: builder.query<
      Credential[],
      { tenantId: string; appId: string; envName: string }
    >({
      query: ({ tenantId, appId, envName }) => ({
        url: `${tenantId}/${appId}/environments/${envName}/credentials`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetCredentialsResponseNoDedupe,
      providesTags: (_result, _error, { appId, envName }) => [
        { type: 'Connector', id: `app-${appId}` },
        { type: 'Connector', id: `env-${envName}` },
      ],
    }),

    // Save credentials
    saveCredentials: builder.mutation<
      void,
      {
        tenantId: string;
        appId: string;
        envId: string;
        key: string;
        data: SaveCredentialRequest;
      }
    >({
      query: ({ tenantId, appId, envId, key, data }) => ({
        url: `${tenantId}/${appId}/environments/${envId}/${key}/credentials`,
        endpoint: 'transformer',
        method: 'POST',
        body: transformSaveCredentialsRequest(data),
      }),
      invalidatesTags: (_result, _error, { appId, envId }) => [
        { type: 'Connector', id: `env-${envId}` },
        { type: 'Connector', id: `app-${appId}` },
      ],
    }),

    // Delete credential
    // Matches dataProtect projectActions.js deleteCredentialFromEnvironment() line 579
    // DELETE {tenantId}/{appId}/credentials/{providerLabel}/{connectionKey}/{environmentId}
    // NOTE: Uses numeric environment ID, NOT environment name
    deleteCredential: builder.mutation<
      void,
      {
        tenantId: string;
        appId: string;
        providerLabel: string;
        connectionKey: string;
        environmentId: string;
      }
    >({
      query: ({ tenantId, appId, providerLabel, connectionKey, environmentId }) => ({
        url: `${tenantId}/${appId}/credentials/${providerLabel}/${connectionKey}/${environmentId}`,
        endpoint: 'transformer',
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'Connector', id: `app-${appId}` }],
    }),

    // Get integration types (providers + wizard types)
    // Matches dataProtect mainActions.js callGetProductizedIntegrationTypesForUser()
    // GET integrationtypes/available → { providers, types, tenantAdoptions, providerProperties }
    getIntegrationTypes: builder.query<IntegrationProvider[], void>({
      query: () => ({
        url: 'integrationtypes/available',
        endpoint: 'transformer',
      }),
      transformResponse: (response: IntegrationTypesResponse) => {
        const { providers, providerProperties } = response;
        const transformed = providers
          .sort((a, b) => {
            if ((a.label || a.type) < (b.label || b.type)) return -1;
            if ((a.label || a.type) > (b.label || b.type)) return 1;
            return 0;
          })
          .map((p) => ({
            ...p,
            properties: providerProperties.filter(
              (pp: IntegrationProviderProperty) => p.type === pp.providerType
            ),
          }));

        return transformed;
      },
    }),

    // Test connection
    // Matches dataProtect environmentActions.js tryCheckConnection() exactly:
    // - SF_AGENT_API_URL → GET {tenantId}/{appId}/environments/{envName}/{connectorKey}/testconnection
    // - TEST_CONN_API_URL → POST to test-connections service
    // - TRANS_API_URL + LingkWebSocket → POST to transformer with envId
    // - Ethos → POST to transformer with envId (body includes key)
    // - Default → POST to transformer_api
    testConnection: builder.mutation<
      TestConnectionResponse,
      {
        tenantId: string;
        appId: string;
        type: string;
        data?: TestConnectionRequest;
        endpoint?: 'testConn' | 'transformer' | 'sfAgent';
        envId?: string;
        envName?: string;
        connectorKey?: string;
      }
    >({
      query: ({
        tenantId,
        appId,
        type,
        data,
        endpoint = 'transformer',
        envId,
        envName,
        connectorKey,
      }) => {
        // SF_AGENT_API_URL: GET with envName/connectorKey URL (environmentActions.js line 717-724)
        if (endpoint === 'sfAgent' && envName && connectorKey) {
          return {
            url: `${tenantId}/${appId}/environments/${envName}/${connectorKey}/testconnection`,
            endpoint: 'sfAgent',
            method: 'GET',
          };
        }

        const url = envId
          ? `${tenantId}/${appId}/${envId}/testconnection/${type.toLowerCase()}`
          : `${tenantId}/${appId}/testconnection/${type.toLowerCase()}`;
        // Ethos requires key in body (environmentActions.js line 730-735)
        let body = data
          ? (transformTestConnectionRequest(data) as Record<string, unknown>)
          : undefined;
        if (type === 'Ethos' && connectorKey && body) {
          body = { ...body, key: connectorKey };
        }
        // TEST_CONN_API_URL and TRANS_API_URL+LingkWebSocket require application/json
        // (environmentActions.js lines 695-699, 711-713)
        const headers: Record<string, string> =
          endpoint === 'testConn' || type === 'LingkWebSocket'
            ? { 'Content-Type': 'application/json' }
            : {};
        return {
          url,
          endpoint,
          method: 'POST',
          body,
          headers,
        };
      },
      transformResponse: transformTestConnectionResponse,
    }),

    // Get file schema
    getFileSchema: builder.query<
      FileSchema[],
      {
        tenantId: string;
        appId: string;
        envName: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ tenantId, appId, envName, limit = 50, offset = 0 }) => ({
        url: `${tenantId}/${appId}/environments/${envName}/fileschema?limit=${limit}&offset=${offset}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetFileSchemaResponse,
    }),

    // Get file schema count
    getFileSchemaCount: builder.query<
      { count: number },
      { tenantId: string; appId: string; envName: string }
    >({
      query: ({ tenantId, appId, envName }) => ({
        url: `${tenantId}/${appId}/environments/${envName}/fileschemacount`,
        endpoint: 'transformer',
      }),
    }),

    // Delete flat file
    deleteFile: builder.mutation<
      void,
      { tenantId: string; appId: string; envId: string; fileName: string }
    >({
      query: ({ tenantId, appId, envId, fileName }) => ({
        url: `${tenantId}/${appId}/${envId}/deletefile/${encodeURIComponent(fileName)}`,
        endpoint: 'transformer',
        method: 'DELETE',
      }),
    }),

    // Get presigned download URL (returns URL string; backend PresignS3Url)
    getDownloadPolicy: builder.query<
      string,
      { tenantId: string; appId: string; envId: string; fileName: string }
    >({
      query: ({ tenantId, appId, envId, fileName }) => ({
        url: `${tenantId}/${appId}/${envId}/downloadpolicy/${encodeURIComponent(fileName)}`,
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown) => {
        // Backend returns JSON-encoded presigned URL string
        return typeof response === 'string' ? response : String(response ?? '');
      },
    }),

    // Get upload policy (body validated by backend SchemasSchema - resources as [{ name, byteSize? }])
    // byteSize must be sent so backend stores it; otherwise files show 0 Bytes
    getUploadPolicy: builder.mutation<
      UploadPolicyResponse,
      {
        tenantId: string;
        appId: string;
        envId: string;
        name: string;
        resources: Array<{ name: string; byteSize?: number }>;
        providerType: string;
      }
    >({
      query: ({ tenantId, appId, envId, name, resources, providerType }) => ({
        url: `${tenantId}/${appId}/${envId}/uploadpolicy`,
        endpoint: 'transformer',
        method: 'POST',
        body: { name, resources, providerType },
      }),
    }),

    // Generate certs
    generateCerts: builder.mutation<unknown, { tenantId: string; appId: string; envId: string }>({
      query: ({ tenantId, appId, envId }) => ({
        url: `${tenantId}/${appId}/${envId}/generatecerts`,
        endpoint: 'transformer',
        method: 'POST',
      }),
    }),

    // Delete websocket certs
    deleteCerts: builder.mutation<void, { tenantId: string; appId: string; envId: string }>({
      query: ({ tenantId, appId, envId }) => ({
        url: `${tenantId}/${appId}/environments/${envId}/certs`,
        endpoint: 'transformer',
        method: 'DELETE',
      }),
    }),

    // Get environment logs
    getEnvironmentLogs: builder.query<
      ExecutionLog[],
      { tenantId: string; appId: string; envId: string; limit?: number }
    >({
      query: ({ tenantId, appId, envId, limit = 100 }) => ({
        url: `${tenantId}/${appId}/logs/environments/${envId}?limit=${limit}`,
        endpoint: 'transformer',
      }),
    }),

    // Generate adapter secret
    // Legacy: POST /{tenantId}/{accountId}/credentials/AdapterSecret/environment/{envName}/generate
    // Response: { credentials: "the_secret_string" }
    generateAdapterSecret: builder.mutation<
      { credentials: string },
      { tenantId: string; appId: string; envName: string }
    >({
      query: ({ tenantId, appId, envName }) => ({
        url: `${tenantId}/${appId}/credentials/AdapterSecret/environment/${envName}/generate`,
        endpoint: 'transformer',
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { appId }) => [{ type: 'Connector', id: `app-${appId}` }],
    }),

    // Exchange OAuth authorization code for tokens
    // Salesforce: POST authCode to sfAgent (transformerActions.js line 1323)
    // Blackboard: POST bsAuthCode to transformer (transformerActions.js line 1337)
    postAuthCode: builder.mutation<
      void,
      {
        authCode: string;
        authCodeRequestGuid: string;
        environmentId?: number;
        apiEndpoint: 'sfAgent' | 'transformer';
        authCodePath: string;
      }
    >({
      query: ({ authCode, authCodeRequestGuid, environmentId, apiEndpoint, authCodePath }) => ({
        url: authCodePath,
        endpoint: apiEndpoint,
        method: 'POST',
        body: {
          authCode,
          authCodeRequestGuid,
          ...(environmentId && { environmentId }),
        },
      }),
    }),

    // Get DocuSign/Salesforce OAuth URL
    // Matches dataProtect environmentActions.js getDocuSignOauthUrl() line 1007
    // GET {tenantId}/{appId}/environments/{envName}/oauthurl/{typeGuid}/{orgType}?key={connectionKey}
    // DocuSign uses 'transformer' endpoint, Salesforce uses 'sfAgent' endpoint
    // typeGuid is hardcoded per provider in dataProtect ConnectorSection.js:
    //   DocuSign: "f249774c-4731-48f7-9fbd-0d79f241fcb2"
    //   Salesforce: "9090b61412a74d97ad9ed02c5fx7e705"
    // PKCE: code_challenge parameter added for Salesforce OAuth compliance (June 2026)
    getOAuthUrl: builder.mutation<
      { url: string; state?: string },
      {
        tenantId: string;
        appId: string;
        envName: string;
        typeGuid: string;
        orgType: string;
        connectionKey: string;
        apiEndpoint?: 'transformer' | 'sfAgent';
        codeChallenge?: string;
      }
    >({
      query: ({
        tenantId,
        appId,
        envName,
        typeGuid,
        orgType,
        connectionKey,
        apiEndpoint,
        codeChallenge,
      }) => {
        let url = `${tenantId}/${appId}/environments/${envName}/oauthurl/${typeGuid}/${orgType}?key=${connectionKey}`;
        if (codeChallenge) {
          url += `&code_challenge=${encodeURIComponent(codeChallenge)}`;
        }
        return {
          url,
          endpoint: apiEndpoint || 'transformer',
        };
      },
    }),

    // Get OAuth authorization URL for BVA/Zoom/Canvas
    // Matches dataProtect transformerActions.js createAuthAppNGetCode() line 1404-1423
    // POST {tenantId}/{appId}/env/{envId}/{connectionKey}/getAppAuthCode/{oauthType}
    getBvaOAuthUrl: builder.mutation<
      { url: string },
      {
        tenantId: string;
        appId: string;
        envId: string;
        connectionKey: string;
        oauthType: 'brightspace' | 'zoom' | 'canvas';
        data: {
          rootUrl: string;
          clientId: string;
          clientSecret: string;
          scope?: string;
          connectionKey: string;
        };
      }
    >({
      query: ({ tenantId, appId, envId, connectionKey, oauthType, data }) => ({
        url: `${tenantId}/${appId}/env/${envId}/${connectionKey}/getAppAuthCode/${oauthType}`,
        method: 'POST',
        body: transformBvaOAuthRequest(data),
        endpoint: 'transformer',
      }),
    }),

    // Check/refresh OAuth credentials after OAuth popup completes
    // Matches dataProtect environmentActions.js checkOauthCreds() line 408
    // GET {tenantId}/{appId}/credentials/{providerType}/{envName}/{connectionKey}
    checkOAuthCreds: builder.query<
      Credential,
      {
        tenantId: string;
        appId: string;
        providerType: string;
        envName: string;
        connectionKey: string;
      }
    >({
      query: ({ tenantId, appId, providerType, envName, connectionKey }) => ({
        url: `${tenantId}/${appId}/credentials/${providerType}/${envName}/${connectionKey}`,
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown) => {
        // Returns single credential object
        return response as Credential;
      },
    }),

    // Get provider metadata (objects, fields, etc.)
    // Matches dataProtect environmentActions.js callGetProviderMetadata() lines 767-841
    // Routes based on metadataApi:
    //   - "TEST_CONN_API_URL" → POST to test-connections service
    //   - OAuth (envName+connectionKey) → GET environments/{envName}/{connectionKey}/{metadataEndpoint}
    //   - Default → GET to {metadataEndpoint}
    getProviderMetadata: builder.mutation<
      unknown,
      {
        tenantId: string;
        appId: string;
        metadataEndpoint: string;
        metadataApi: string;
        credentialType: string;
        credentialsJson: Record<string, unknown>;
        providerType: string;
        envName?: string;
        connectionKey?: string;
      }
    >({
      query: ({
        tenantId,
        appId,
        metadataEndpoint,
        metadataApi,
        credentialType,
        credentialsJson,
        providerType,
        envName,
        connectionKey,
      }) => {
        // Map legacy config key names to RTK Query endpoint keys
        // dataProtect uses config[metadataApi] where metadataApi is e.g. "SF_AGENT_API_URL"
        const apiEndpointMap: Record<string, 'transformer' | 'sfAgent' | 'testConn'> = {
          SF_AGENT_API_URL: 'sfAgent',
          TEST_CONN_API_URL: 'testConn',
          TRANS_API_URL: 'transformer',
        };
        const resolvedEndpoint = apiEndpointMap[metadataApi] || 'transformer';

        if (metadataApi === 'TEST_CONN_API_URL') {
          // Normalize creds before sending (matches old UI addLeadingSlashToSftpPath)
          const normalizedCreds = normalizeCredentialsForTestConn(credentialsJson);
          return {
            url: `${tenantId}/${appId}/${metadataEndpoint}`,
            endpoint: 'testConn',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: {
              credentialType,
              credentialsJson: normalizedCreds,
              providerLabel: providerType,
            },
          };
        }

        // Adapter metadata: GET environments/{envName}/adaptermetadata?url={rootUrl}
        // (environmentActions.js lines 790-796, 821-827)
        if (metadataEndpoint === 'adaptermetadata') {
          const rootUrl = credentialsJson.rootUrl as string;
          const urlSuffix = `adaptermetadata?url=${encodeURIComponent(rootUrl)}`;
          if (envName) {
            return {
              url: `${tenantId}/${appId}/environments/${envName}/${urlSuffix}`,
              endpoint: resolvedEndpoint,
            };
          }
          // Fallback without environment (shouldn't happen for adapters)
          return {
            url: `${tenantId}/${appId}/${urlSuffix}`,
            endpoint: resolvedEndpoint,
          };
        }

        // OAuth: GET environments/{envName}/{connectionKey}/{metadataEndpoint}
        if (envName && connectionKey) {
          return {
            url: `${tenantId}/${appId}/environments/${envName}/${connectionKey}/${metadataEndpoint}`,
            endpoint: resolvedEndpoint,
          };
        }
        // Default: GET tenantId/appId/metadataEndpoint
        return {
          url: `${tenantId}/${appId}/${metadataEndpoint}`,
          endpoint: resolvedEndpoint,
        };
      },
    }),

    // Get config templates
    getConfigTemplates: builder.query<ConfigTemplate[], void>({
      query: () => ({
        url: 'config-templates',
        endpoint: 'transformer',
      }),
      transformResponse: (response: unknown) => {
        // Returns array of { Name: string } objects
        const templates = response as Array<{ Name: string }>;
        return templates.map((t) => ({ name: t.Name }));
      },
    }),
  }),
});

export const {
  useGetEnvironmentsQuery,
  useGetEnvironmentOverviewQuery,
  useGetEnvironmentDependenciesQuery,
  useCreateEnvironmentMutation,
  useUpdateEnvironmentMutation,
  useDeleteEnvironmentMutation,
  useToggleEnvironmentPauseMutation,
  useReorderEnvironmentsMutation,
  useGetCredentialsQuery,
  useGetCredentialsRawQuery,
  useSaveCredentialsMutation,
  useDeleteCredentialMutation,
  useTestConnectionMutation,
  useGetIntegrationTypesQuery,
  useGetFileSchemaQuery,
  useGetFileSchemaCountQuery,
  useLazyGetDownloadPolicyQuery,
  useDeleteFileMutation,
  useGetUploadPolicyMutation,
  useGenerateCertsMutation,
  useDeleteCertsMutation,
  useGetEnvironmentLogsQuery,
  useGenerateAdapterSecretMutation,
  useGetProviderMetadataMutation,
  useGetConfigTemplatesQuery,
  useGetOAuthUrlMutation,
  useGetBvaOAuthUrlMutation,
  useLazyCheckOAuthCredsQuery,
  usePostAuthCodeMutation,
} = environmentsApi;
